import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, ArrowLeft, Save, Loader2, Star } from "lucide-react";
import ImageUpload from "@/components/common/ImageUpload";

const empty = {
  name: "", slug: "", logo_url: "", country: "", description: "",
  featured: false, meta_title: "", meta_description: "",
};

const slugify = (s) => s.toString().toLowerCase().trim()
  .replace(/[^a-z0-9\s-]/g, "")
  .replace(/\s+/g, "-").replace(/-+/g, "-");

export default function BrandsAdmin() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [page, setPage] = useState(1);
  const perPage = 20;

  const load = () => {
    setBusy(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    api.get(`/admin/brands?${params.toString()}`).then((r) => setRows(r.data)).finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const paged = useMemo(() => {
    const start = (page - 1) * perPage;
    return rows.slice(start, start + perPage);
  }, [rows, page]);
  const totalPages = Math.max(1, Math.ceil(rows.length / perPage));

  const save = async () => {
    if (!editing) return;
    if (!editing.name || !editing.slug) return toast.error("name and slug are required");
    try {
      const isEdit = editing._existing;
      if (isEdit) await api.put(`/admin/brands/${editing.slug}`, editing);
      else await api.post("/admin/brands", editing);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (slug) => {
    if (!window.confirm(`Delete brand "${slug}"? Cars keep their brand_name but lose the brand record.`)) return;
    await api.delete(`/admin/brands/${slug}`);
    load();
  };

  if (editing) {
    return (
      <div data-testid="admin-brands-edit">
        <button onClick={() => setEditing(null)} className="text-sm text-blue-600 inline-flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4"/> Back to brands
        </button>
        <h1 className="font-display text-3xl tracking-tighter">{editing._existing ? "Edit brand" : "New brand"}</h1>
        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name *">
                <input data-testid="brand-name" value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value, slug: editing._existing ? editing.slug : slugify(e.target.value) })}
                  className={inp}/>
              </Field>
              <Field label="Slug *">
                <input data-testid="brand-slug" value={editing.slug} disabled={editing._existing}
                  onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })}
                  className={inp + (editing._existing ? " opacity-60" : "")}/>
              </Field>
            </div>
            <Field label="Country">
              <input data-testid="brand-country" value={editing.country || ""}
                onChange={(e) => setEditing({ ...editing, country: e.target.value })} className={inp}/>
            </Field>
            <Field label="Description">
              <textarea data-testid="brand-description" value={editing.description || ""}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                className={`${inp} min-h-[120px] py-2`}/>
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="brand-featured" checked={!!editing.featured}
                onChange={(e) => setEditing({ ...editing, featured: e.target.checked })}/>
              Featured brand (shows in homepage brand strip)
            </label>
          </div>
          <div className="space-y-4">
            <Field label="Logo">
              <ImageUpload value={editing.logo_url} onChange={(v) => setEditing({ ...editing, logo_url: v })} folder="brands" testId="brand-logo"/>
            </Field>
            <div className="rounded-3xl border border-[#E5E7EB] bg-white p-4 space-y-3">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">SEO</div>
              <Field label="Meta title">
                <input data-testid="brand-meta-title" value={editing.meta_title || ""}
                  onChange={(e) => setEditing({ ...editing, meta_title: e.target.value })} className={inp}/>
              </Field>
              <Field label="Meta description">
                <textarea data-testid="brand-meta-desc" value={editing.meta_description || ""}
                  onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })} className={`${inp} min-h-[80px] py-2`}/>
              </Field>
            </div>
            <button onClick={save} data-testid="brand-save" className="btn-primary w-full h-11 text-sm inline-flex items-center justify-center gap-2">
              <Save className="w-4 h-4"/> Save brand
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-brands">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-tighter">Brands</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage OEM brands, logos and featured status.</p>
        </div>
        <button data-testid="brand-new" onClick={() => setEditing({ ...empty })}
          className="btn-primary h-10 px-4 text-sm inline-flex items-center gap-2">
          <Plus className="w-4 h-4"/> New brand
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
          <input data-testid="brand-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search name, slug or country…"
            className="h-10 pl-9 pr-3 rounded-full border border-[#E5E7EB] bg-white text-sm outline-none focus:border-blue-400 w-full"/>
        </div>
      </div>

      <div className="mt-4 rounded-3xl border border-[#E5E7EB] overflow-hidden bg-white">
        {busy ? (
          <div className="grid place-items-center py-16 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin"/></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground text-sm">No brands yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC]">
              <tr>
                <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Brand</th>
                <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Country</th>
                <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Cars</th>
                <th className="text-left p-3 text-xs uppercase tracking-[0.15em] text-muted-foreground">Featured</th>
                <th className="p-3"/>
              </tr>
            </thead>
            <tbody>
              {paged.map((b) => (
                <tr key={b.slug} data-testid={`brand-row-${b.slug}`} className="border-t border-[#E5E7EB]">
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      {b.logo_url ? (
                        <img src={b.logo_url} alt={b.name} className="w-9 h-9 rounded-lg object-contain bg-[#F3F6FA] border border-[#E5E7EB]"/>
                      ) : (
                        <div className="w-9 h-9 rounded-lg bg-[#F3F6FA] border border-[#E5E7EB] grid place-items-center text-[10px] text-muted-foreground">{b.name?.[0]}</div>
                      )}
                      <div>
                        <div className="font-medium">{b.name}</div>
                        <div className="text-xs text-muted-foreground">{b.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-muted-foreground">{b.country || "—"}</td>
                  <td className="p-3 font-mono">{b.car_count ?? 0}</td>
                  <td className="p-3">{b.featured ? <Star className="w-4 h-4 fill-amber-400 text-amber-400"/> : <span className="text-muted-foreground">—</span>}</td>
                  <td className="p-3 text-right">
                    <button onClick={() => setEditing({ ...b, _existing: true })} data-testid={`brand-edit-${b.slug}`} className="text-sm text-blue-600 hover:underline mr-3">Edit</button>
                    <button onClick={() => del(b.slug)} data-testid={`brand-del-${b.slug}`} className="text-sm text-rose-600 hover:underline">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {rows.length > perPage && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <div className="text-muted-foreground">Page {page} of {totalPages} · {rows.length} brands</div>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="brand-prev"
              className="h-9 px-3 rounded-full border border-[#E5E7EB] bg-white disabled:opacity-50">Prev</button>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="brand-next"
              className="h-9 px-3 rounded-full border border-[#E5E7EB] bg-white disabled:opacity-50">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = "w-full h-10 rounded-xl border border-[#E5E7EB] bg-white px-3 text-sm outline-none focus:border-[#2563EB]";
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
